import type { FieldValues, Message, Path, UseControllerProps } from 'react-hook-form';
import { OptionWithHint } from '#components/patterns/forms/select/option/OptionWithHint.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import type { Host } from '#dn-types/host.ts';
import { SelectAsyncSearchableControlled } from '../../forms/select/select-async-searchable/SelectAsyncSearchableControlled.tsx';
import { makeAllHostsQueryOptions, type NoneOption } from './SelectHost.queries.ts';

type Props<T extends FieldValues, N extends Path<T>> = {
  errorMessage?: Message;
  noneOption?: NoneOption;
  label: string;
  className?: string;
  filterHosts?: (host: Host) => boolean;
} & UseControllerProps<T, N> &
  Omit<
    React.ComponentProps<typeof SelectAsyncSearchableControlled>,
    'getItemsQueryOptions' | 'control' | 'renderOptions'
  >;

export function SelectHost<T extends FieldValues, N extends Path<T>>({
  label,
  errorMessage,
  noneOption,
  filterHosts,
  control,
  name,
  className,
  ...props
}: Props<T, N>) {
  return (
    <SelectAsyncSearchableControlled
      className={className}
      getItemsQueryOptions={makeAllHostsQueryOptions(noneOption, filterHosts)}
      control={control}
      name={name}
      label={label}
      placeholder="Please select…"
      searchKeys={['label', 'hint']}
      errorMessage={errorMessage}
      renderOptions={({ onChange, items }) => (
        <>
          {items.map((itm) => (
            <SelectItem key={itm.value} value={itm.value} onClick={() => onChange(itm.value)} searchable={true}>
              {itm.hint ?
                <OptionWithHint value={itm.label} hint={itm.hint} />
              : itm.label}
            </SelectItem>
          ))}
        </>
      )}
      {...props}
    />
  );
}
